import { DateTime } from 'luxon'
import AuthMixin from '@/components/mixins/auth-mixin'
import OnEnterMixin from '@/components/mixins/onenter-mixin'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { scheduleStore } from '@/pinia/stores/schedule/scheduleStore'

export default {
  name: 'scheduleSearchBar',
  inject: [
    '$utils',
    '$apiError'
  ],
  components: {},
  props: {
    presentPlayerListProp: {
      type: Object,
      default () { return {} }
    },
    playerSelectedFromParentProp: {
      default () { return [] }
    },
    tagsProp: {
      type: Array,
      default () { return [] }
    },
    selectedDayProp: {
      default () { return [] }
    },
    isActive: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    presentPlayerListProp (newVal) {
      this.presentPlayerListByBatch = newVal.batchHistory

      if (this.originalPresentPlayerListBeforeTheSearch !== null) {
        const originalPresentPlayerListBeforeTheSearch = this.$utils.clone(this.originalPresentPlayerListBeforeTheSearch)

        originalPresentPlayerListBeforeTheSearch.batchHistory.forEach((playerListByBatch) => {
          for (const indx in newVal.itemsToShow) {
            const currentPlayerItem = newVal.itemsToShow[indx]
            playerListByBatch = this.$utils.updateItemFromList(playerListByBatch, currentPlayerItem, 'projectId')
          }
        })

        for (const indx in newVal.itemsToShow) {
          const currentPlayerItem = newVal.itemsToShow[indx]
          originalPresentPlayerListBeforeTheSearch.itemsToShow = this.$utils.updateItemFromList(originalPresentPlayerListBeforeTheSearch.itemsToShow, currentPlayerItem, 'serialNumber')
        }

        this.originalPresentPlayerListBeforeTheSearch = originalPresentPlayerListBeforeTheSearch
      }
    },
    searchTextVal (val) {
      this.searchTextVal = val
      this.handlePreFlightSearchFromPresentData()
      this.confirmedSearchedValFlag = false
      this.emitEvent('NEW_PLAYER_SEARCH_TEXT', {
        text: val
      })
    },
    playerStatusSelected: {
      handler (val) {
        this.confirmedSearchedValFlag = false
      },
      deep: true
    },
    playerSelectedFromParentProp: {
      handler (val) {
        this.playerSelectedFromParent = val
      },
      deep: true
    },
    tagsProp: {
      handler (newTagsVal) {
        this.tags = newTagsVal

        if (this.tags.length <= 0) {
          if (this.searchTextVal.trim() === '') {
            this.emitEvent('RESET_TO_ORIGINAL_LISTS', {
              searchResults: [],
              reqSearchFilter: { search: '', type: null },
              afterAPIRequest: null,
              tags: []
            })
          }

          return
        }

        if (this.originalPresentPlayerListBeforeTheSearch === null) {
          this.originalPresentPlayerListBeforeTheSearch = this.presentPlayerListProp
        }
        this.handlePreFlightSearchFromPresentData()
        // Fetch from backend if user has seconds idle time in type selection
        let timer = null
        clearTimeout(timer)
        timer = setTimeout(() => {
          this.fetchPlayerListFromBackend({
            onKeyupIdleTimeRequest: true
          })
        }, this.idleTimeSearchingVal)
      },
      deep: true
    },
    selectedDayProp: {
      handler (newVal) {
        this.selectedDay = newVal
      },
      deep: true
    }
  },
  data () {
    return {
      error: '',
      searchStatus: { confirmedSearch: 'confirmedSearch', unConfirmedSearch: 'unConfirmedSearch' },
      originalPresentPlayerListBeforeTheSearch: null,
      presentPlayerListByBatch: this.presentPlayerListProp.batchHistory,
      playerSelectedFromParent: this.playerSelectedFromParentProp,
      enableSearchOptions: false,
      searchTextVal: '',
      searchResultsFromFrontend: { fullDetailsList: [], serialNumberList: [], tags: [] },
      searchResultsFromBackend: { fullDetailsList: [], tags: [] },
      searchResultsSummary: [],
      allowPlayerStatusSelectionInConfirmedSearch: false,
      playerStatusSelected: [],
      confirmedSearchedValFlag: false,
      idleTimeSearchingVal: 2000, // 2 seconds,
      mouseOverConfirmedText: false,
      tags: this.tagsProp,
      selectedDay: this.selectedDayProp
    }
  },
  mixins: [AuthMixin, OnEnterMixin],
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('schedule-search-bar-change', { action, payload: { data } })
      }
    },
    handleSearchIconClick () {
      useMixpanel().track('Search clicked')

      this.originalPresentPlayerListBeforeTheSearch = this.presentPlayerListProp
      this.enableSearchOptions = true
      this.$nextTick(_ => {
        this.$refs.scheduleSearchInput.focus()
      })
    },
    fetchPlayerListFromBackend (params) {
      const apiRequest = async () => {
        this.$utils.mainLoader(true)
        const start = DateTime.fromISO(this.selectedDay.fullDate, { zone: 'utc' }).startOf('day').toISO()
        const dur = 24 * 3600 // 24H
        const requestParams = {
          start,
          dur,
          width: 512,
        }

        // Set search value
        if (this.searchTextVal !== '') {
          requestParams.search = this.searchTextVal
          useMixpanel().track('Search text')
        }

        // set tags
        if (this.tags.length > 0) {
          requestParams.tags = JSON.stringify(this.tags)
        }

        // Capture tags at request time for race-condition check
        const tagsAtRequestTime = requestParams.tags ?? null

        this.error = ''
        const store = scheduleStore()
        const result = await store.fetchExtendedSchedules(requestParams)

        if (result.error) {
          this.error = result.error.message
          this.$utils.mainLoader(false)
          this.$apiError.handleUnauthorizedError(result.error, { originalRequest: () => { apiRequest() } })
          this.emitEvent('SEARCH_REQUEST_FAILED', {})
          return
        }

        const newPlayerBatch = result.data.items
        const selectedTags = (Array.isArray(this.tags) && this.tags.length) ? JSON.stringify(this.tags) : null

        /** Process search results
         - Request tags and current tags selection is the same
         - Make sure no selection have been made in parent component to avoid data refresh
         - Make sure current search options is the same with the search options before the request to avoid flicker
        */
        if (tagsAtRequestTime === selectedTags) {
          if (params.onKeyupIdleTimeRequest) {
            if (!this.playerSelectedFromParent.length && (this.enableSearchOptions || this.tags.length > 0)) {
              const prevSearchTextVal = requestParams.search ?? ''
              const currentSearchTextVal = this.searchTextVal

              if (prevSearchTextVal === currentSearchTextVal) {
                this.searchResultsFromBackend.fullDetailsList = newPlayerBatch

                // Set tags
                newPlayerBatch.forEach((playerItem) => {
                  if (playerItem.tags) {
                    this.searchResultsFromBackend.tags = [...new Set([...this.searchResultsFromBackend.tags, ...playerItem.tags])]
                  }
                })

                this.sendSearchResultsToParent({ afterAPIRequest: true })
              }
            }
          }

          if (!params.onKeyupIdleTimeRequest) {
            this.searchResultsFromBackend.fullDetailsList = newPlayerBatch

            // Set tags
            newPlayerBatch.forEach((playerItem) => {
              if (playerItem.tags) {
                this.searchResultsFromBackend.tags = [...new Set([...this.searchResultsFromBackend.tags, ...playerItem.tags])]
              }
            })

            this.sendSearchResultsToParent({ afterAPIRequest: true })
          }
        }

        this.$utils.mainLoader(false)
        // Process callback
        if (params?.callback) {
          params.callback()
        }
      }
      apiRequest()
    },
    updateCurrentUrl () {
      const query = this.$route.query

      if (this.searchTextVal.trim().length > 0) {
        query.search = this.searchTextVal.trim()
      }

      if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        this.$router.push({
          path: '/schedule',
          query
        })
      }
    },
    handleSearchConfirmation (actionType) {
      const searchTextVal = this.searchTextVal.trim()
      const hasSearchText = searchTextVal.length > 0
      const hasPlayerStatusSelected = this.playerStatusSelected.length > 0
      const isOnEnterOrOnBlur = ['onEnter', 'onBlur'].indexOf(actionType) >= 0
      const isOnSelectPlayerStatus = actionType === 'selectPlayerStatus'
      let searchFromBackend = false

      this.emitEvent('NEW_PLAYER_SEARCH_TEXT_CONFIRM', {
        text: searchTextVal
      })

      if (isOnEnterOrOnBlur) {
        if (!hasSearchText && !hasPlayerStatusSelected) {
          this.handleResetSearchValue()
          this.enableSearchOptions = false
          return
        }

        /* If user click on the file type BEFORE entering any text, then apply the file filter and let user type text
        (and then confirm the search using enter or focus out) */
        if (hasSearchText && hasPlayerStatusSelected) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }

        if (!hasSearchText && hasPlayerStatusSelected) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }

        /* If the user enter some text and press enter, then show the files filters and if the user clicks on one of them,
        apply it together with the text previously entered by the user.  */
        if (hasSearchText && !hasPlayerStatusSelected) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }
        this.updateCurrentUrl()
      }

      if (isOnSelectPlayerStatus) {
        if (!hasPlayerStatusSelected && !hasSearchText) {
          this.handleResetSearchValue()
          return
        }

        /* If the user click on the file type when some text have been entered, then validate the text and show the tag
          but let the files filter visible and apply the file filter the user
        */
        if (hasPlayerStatusSelected && hasSearchText) {
          this.allowPlayerStatusSelectionInConfirmedSearch = true
          searchFromBackend = true
        }
        this.updateCurrentUrl()
      }

      if (searchFromBackend && this.confirmedSearchedValFlag) {
        return
      }

      // Fetch from backend
      if (searchFromBackend) {
        this.updateCurrentUrl()
        this.confirmedSearchedValFlag = true
        this.fetchPlayerListFromBackend({
          callback: () => {
            if (this.playerStatusSelected.length === 0) {
              this.allowPlayerStatusSelectionInConfirmedSearch = true
            }
          }
        })
      }
    },
    /**
     * Handle client side search from present data fetch from backend or called as listByBatch
     */
    handlePreFlightSearchFromPresentData () {
      if (!this.originalPresentPlayerListBeforeTheSearch) {
        return
      }

      const dataSource = this.$utils.clone(this.originalPresentPlayerListBeforeTheSearch.batchHistory)
      const playerStatusSource = this.playerStatusSelected
      let preflightSearchResults = {
        fullDetailsList: [],
        serialNumberList: [],
        tags: []
      }
      const searchText = this.searchTextVal.trim()
      const searchByName = (searchText.length > 0 && playerStatusSource.length === 0 && this.tags.length === 0)
      const searcyByNameAndType = (searchText.length > 0 && playerStatusSource.length > 0 && this.tags.length === 0)
      const searcyByNameAndTags = (searchText.length > 0 && playerStatusSource.length === 0 && this.tags.length > 0)
      const searchByNameAndTypeAndTags = (searchText.length > 0 && this.tags.length > 0 && this.tags.length > 0)
      const searchByFileType = (searchText.length === 0 && playerStatusSource.length > 0 && this.tags.length === 0)
      const searchByFileTypeAndTags = (searchText.length === 0 && playerStatusSource.length > 0 && this.tags.length > 0)
      const searchByTags = (searchText.length === 0 && playerStatusSource.length === 0 && this.tags.length > 0)
      const hasMultiTagSelection = this.tags.length > 1
      const hasSingleTagSelection = this.tags.length === 1
      const allPlayerFromEachBatch = {
        fullDetailsList: [],
        serialNumberList: [],
        tags: []
      }

      for (const playerBatch of dataSource) {
        for (const playerBatchIndx in playerBatch) {
          const playerItem = playerBatch[playerBatchIndx]
          const playerIncludesName = playerItem.name.toLowerCase().includes(searchText.toLowerCase())
          const playerIncludesStatus = (playerStatusSource.indexOf(playerItem.type) >= 0)
          let playerIncludesTag = false

          // Check tags
          if (!playerItem.tags) playerItem.tags = []

          if (hasMultiTagSelection) {
            playerIncludesTag = this.tags.every((selectedTag) => { return playerItem.tags.includes(selectedTag) })
          } else if (hasSingleTagSelection) {
            for (const indx in playerItem.tags) {
              if (this.tags[0] === playerItem.tags[indx]) {
                playerIncludesTag = true
                break
              }
            }
          } else {
            playerIncludesTag = false
          }

          let addToResult = false

          if (searchByName && playerIncludesName) { // Search by name
            addToResult = true
          } else if (searchByFileType && playerIncludesStatus) { // Search by type
            addToResult = true
          } else if (searchByTags && playerIncludesTag) {
            addToResult = true
          } else if (searcyByNameAndType && (playerIncludesName && playerIncludesStatus)) {
            addToResult = true
          } else if (searcyByNameAndTags && (playerIncludesName && playerIncludesTag)) {
            addToResult = true
          } else if (searchByFileTypeAndTags && (playerIncludesStatus && playerIncludesTag)) {
            addToResult = true
          } else if (searchByNameAndTypeAndTags && (playerIncludesName && playerIncludesStatus && playerIncludesTag)) {
            addToResult = true
          }

          if (addToResult) {
            if (!preflightSearchResults.fullDetailsList.find((s) => { return s.scheduleId === playerItem.scheduleId })) {
              preflightSearchResults.fullDetailsList.push(playerItem)

              // Set tags
              if (playerItem.tags) {
                preflightSearchResults.tags = [...new Set([...preflightSearchResults.tags, ...playerItem.tags])]
              }
            }

            if (preflightSearchResults.serialNumberList.indexOf(playerItem.scheduleId) < 0) {
              preflightSearchResults.serialNumberList.push(playerItem.scheduleId)
            }
          }

          // keep original list to send if no results
          if (!allPlayerFromEachBatch.fullDetailsList.find((p) => { return p.scheduleId === playerItem.scheduleId })) {
            allPlayerFromEachBatch.fullDetailsList.push(playerItem)

            if (playerItem.tags) {
              preflightSearchResults.tags = [...new Set([...preflightSearchResults.tags, ...playerItem.tags])]
            }
          }
          if (allPlayerFromEachBatch.serialNumberList.indexOf(playerItem.scheduleId) < 0) {
            allPlayerFromEachBatch.serialNumberList.push(playerItem.scheduleId)
          }
        }
      }

      if (searchText.length === 0 && playerStatusSource.length === 0 && this.tags.length === 0) {
        preflightSearchResults = allPlayerFromEachBatch
      }

      this.searchResultsFromFrontend = preflightSearchResults
      this.searchResultsFromBackend = { fullDetailsList: [], tags: [] }
      this.sendSearchResultsToParent()
    },
    sendSearchResultsToParent (params) {
      if (!this.enableSearchOptions && this.allowPlayerStatusSelectionInConfirmedSearch) {
        this.searchResultsFromFrontend = { fullDetailsList: [], serialNumberList: [], tags: [] }
      }

      const searchResultsFromFrontend = this.$utils.clone(this.searchResultsFromFrontend)
      const searchResultsFromBackend = this.$utils.clone(this.searchResultsFromBackend)
      const searchResultsSummary = searchResultsFromFrontend
      let searchResultsTags = searchResultsFromFrontend.tags

      searchResultsFromBackend.fullDetailsList.forEach((playerItemBackend) => {
        if (searchResultsFromFrontend.serialNumberList.indexOf(playerItemBackend.scheduleId) < 0) {
          searchResultsSummary.fullDetailsList.push(playerItemBackend)
          searchResultsSummary.serialNumberList.push(playerItemBackend.scheduleId)

          // Set Tags
          if (playerItemBackend.tags) {
            searchResultsTags = [...new Set([...searchResultsTags, ...playerItemBackend.tags])]
          }
        }
      })

      this.emitEvent('NEW_PLAYER_SEARCH_RESULTS', {
        searchResults: searchResultsSummary,
        reqSearchFilter: { search: this.searchTextVal, type: this.playerStatusSelected },
        afterAPIRequest: (typeof params === 'object' && params.afterAPIRequest),
        tags: searchResultsTags
      })
    },
    handleResetSearchValue () {
      this.searchTextVal = ''
      this.enableSearchOptions = true
      this.allowPlayerStatusSelectionInConfirmedSearch = false
      this.playerStatusSelected = []
      this.resetToOriginalPresentData()
    },
    resetToOriginalPresentData () {
      // Reset results from list history
      const originalPlayerList = this.$utils.clone(this.originalPresentPlayerListBeforeTheSearch)
      this.emitEvent('RESET_TO_ORIGINAL_LISTS', {
        originalPlayerList,
        reqSearchFilter: { search: null, type: [] }
      })
    },
    handleSearhOptionOnBlur () {
      const self = this
      $('#schedulePage').click((e) => {
        const targetElement = $(e.target)
        const preventConfirmedSearch = targetElement.hasClass('prevent-confirmed-search')
        const forceToCloseSearchOption = targetElement.hasClass('player-item-cover')

        if (self.enableSearchOptions && !preventConfirmedSearch && !forceToCloseSearchOption) {
          self.handleSearchConfirmation('onBlur')
        } else if (forceToCloseSearchOption) {
          if (self.searchTextVal.length > 0) {
            self.enableSearchOptions = true
          }
        }
      })
    },
    /**
     * Auto search from backend after seconds when user stop typing
     */
    handleAutoSearchFromBackendWhenStopTyping () {
      let timer = null
      this.$refs.scheduleSearchBar.onkeyup = () => {
        clearTimeout(timer)
        timer = setTimeout(() => {
          this.fetchPlayerListFromBackend({
            onKeyupIdleTimeRequest: true
          })
        }, this.idleTimeSearchingVal)
      }
    },
    // Auto search confirmation if press enter even if the input search field is not onFocus state
    handleSearchConfirmatioOnPagePressEnter () {
      const self = this
      $(document).keypress((event) => {
        const keycode = (event.keyCode ? event.keyCode : event.which)
        if (keycode.toString() === '13' && self.enableSearchOptions) {
          self.handleSearchConfirmation('onEnter')
        }
      })
    },
    handleConfirmedTextValueClick () {
      this.enableSearchOptions = true
    },
    onEnter (event) {
      if (event.target.id === 'schedule-search-input') {
        this.handleSearchConfirmation('onEnter')
      }
    }
  },
  mounted () {
    if (this.$route.query.search) {
      this.searchTextVal = this.$route.query.search
      this.enableSearchOptions = true
    }

    this.handleSearhOptionOnBlur()
    this.handleAutoSearchFromBackendWhenStopTyping()
    this.handleSearchConfirmatioOnPagePressEnter()
  }
}
